import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged, filter, fromEvent, map, shareReplay, startWith, switchMap, take, tap } from 'rxjs';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { PlaceRef, PlacesService, SearchSuggestion } from '../explore/places.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { PlaceComponent } from '@app/where/place/place.component';
import { ApiService, ExploreCityRef } from '@app/services/api/api.service';
import { HostelComponent } from '@app/where/hostel/hostel.component';

@Component({
  selector: 'app-where-mobile',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, AngularSvgIconModule, NgOptimizedImage, PlaceComponent, HostelComponent],
  templateUrl: './where-mobile.component.html',
  styleUrls: ['./where-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: WhereMobileComponent
    }
  ],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ transform: 'translateY(-8px)', opacity: 0.4 }),
        animate('0.15s ease-in',
          style({ transform: 'translateY(0)', opacity: 1 }))
      ])
    ]),
  ]
})
export class WhereMobileComponent implements ControlValueAccessor {
  // @Input() public value: any;
  @Input() public activeInput: string | undefined;
  @Output() public activeInputChange = new EventEmitter<string | undefined>();

  public places?: Observable<any>;
  public searching = new BehaviorSubject(false);
  public focused = new BehaviorSubject(false);
  public query!: Observable<string>;

  // todo control valuea accessor on each element
  @ViewChild('whereInput')
  public whereInput?: ElementRef;

  @ViewChild('carouselBounds')
  public carouselBounds!: ElementRef;

  @ViewChild('carouselContainer')
  public carouselContainer!: ElementRef;

  @ViewChild('carouselLeftControl')
  public carouselLeftControl!: ElementRef;

  @ViewChild('carouselRightControl')
  public carouselRightControl!: ElementRef;

  @ViewChildren('carouselItem')
  public carouselItems: ElementRef[] = [];

  public bottomSheetOpen = new BehaviorSubject(false);

  showSearches = new BehaviorSubject(false);
  selection: any;
  focus?: string;

  private observerLeft?: IntersectionObserver;
  private observerRight?: IntersectionObserver;

  private _value: any;

  get value(): string {
    return this._value;
  }

  @Input()
  set value(val: ExploreCityRef | PlaceRef) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  constructor(
    private placesService: PlacesService,
    private apiService: ApiService
  ) {}

  ngOnInit() {
    this.cities = this.apiService.getExploreCities().pipe(
      take(1),
      map((resp) => resp.data.data)
    );
  }

  get displayValue(): string {
    if (!this._value) {
      return '';
    } else if (this.isHostel(this._value)) {
      return `${this._value.name}`;
    }

    return this._value && this._value.cityName
      ? this._value.cityName
      : this._value && this._value.city
      ? this._value.city
      : this._value.state
      ? `${this._value.state}, ${this._value.country}`
      : this._value.country;
  }

  public isHostel(val: SearchSuggestion): boolean {
    if (!val) {
      return false;
    }

    console.log(val);

    return val.searchType === 2;
  }

  ngAfterViewInit() {
    const firstItem = Array.from(this.carouselItems)[0];
    const lastItem = Array.from(this.carouselItems)[7];

    if (firstItem && this.carouselLeftControl) {
      const native = firstItem?.nativeElement;

      this.observerLeft = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) {
            this.carouselLeftControl.nativeElement.setAttribute('disabled', true);
            this.carouselBounds.nativeElement.classList.remove('mask-left');
          } else {
            this.carouselBounds.nativeElement.classList.add('mask-left');
            this.carouselLeftControl.nativeElement.disabled = false;
          }
        },
        {
          root: this.carouselBounds.nativeElement,
          threshold: [0.5]
        }
      );

      this.observerLeft.observe(native);
    }

    if (lastItem && this.carouselRightControl) {
      const native = lastItem?.nativeElement;

      this.observerRight = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) {
            this.carouselRightControl.nativeElement.setAttribute('disabled', true);
            this.carouselBounds.nativeElement.classList.remove('mask-right');
          } else {
            this.carouselBounds.nativeElement.classList.add('mask-right');
            this.carouselRightControl.nativeElement.disabled = false;
          }
        },
        {
          root: this.carouselBounds.nativeElement,
          threshold: [0.5]
        }
      );

      this.observerRight.observe(native);
    }

    this.query = fromEvent(this.whereInput!.nativeElement, 'keyup')
      .pipe(
        map((event: any) => event.target.value),
      );

    this.places = this.query
      .pipe(
        tap(() => {
          this.showSearches.next(true);
        }),
        debounceTime(100),
        distinctUntilChanged(),
        filter(term => Boolean(term)),
        tap((query: string) => {
          this.searching.next(true);
          this.showSearches.next(true);
        }),
        switchMap((term) => this.placesService.search(term)),
        map((resp) => {
          this.searching.next(false);
          this.showSearches.next(true);
  
          // map the resp.cities into PlaceRefs that only have the country and countryId
          // remove any duplicates
          //city: string;
          // cityId: string;
          // stateId: string;
          // countryId: string;
          // country: string;
          // searchType: number;
          const flattenedCountries = resp.countries.map((city) => {
            return {
              countryId: city.countryId,
              country: city.country,
              searchType: 4,
              city: null,
              cityId: null,
              stateId: null,
            };
          });
  
          // remove any duplicates
          const uniqueCountries = flattenedCountries.filter(
            (country, index, self) =>
              index === self.findIndex((t) => t.countryId === country.countryId)
          );
  
  
          const results = [
            ...uniqueCountries,
            ...resp.countries,
            ...resp.cities,
            ...resp.states,
            ...resp.hostels,
          ];

          return results;
        }),
        startWith([]),
        shareReplay({
          bufferSize: 1,
          refCount: true
        })
      );
  }

  public openBottomSheet() {
    this.bottomSheetOpen.next(true);
  }

  public closeBottomSheet() {
    this.bottomSheetOpen.next(false);
  }

  setFocus(focus: boolean) {
    this.focused.next(focus);
  }

   public trackByCity(i: any, a: any) {
    return a.city;
   }

   onChange = (value: ExploreCityRef | PlaceRef) => {};
   onTouched = () => {};

   private touched = false;

   public onBlurHandler() {
     if (!this.touched) {
       this.onTouched();
       this.touched = true;
     }
   }

   public writeValue(value: any): void {
     this.value = value;
   }

   public registerOnChange(onChange: any) {
     this.onChange = onChange;
   }

   public registerOnTouched(onTouched: any) {
     this.onTouched = onTouched;
   }

   scrollRight() {
    this.carouselContainer.nativeElement.scrollBy({
      top: 0,
      left: 420,
      behavior: 'smooth',
    });
   }

   scrollLeft() {
    this.carouselContainer.nativeElement.scrollBy({
      top: 0,
      left: -420,
      behavior: 'smooth',
    });
   }

   public doUpdate(city: ExploreCityRef | PlaceRef) {
    this.onChange(city);
    this.value = city;
    this.showSearches.next(false);
    this.searching.next(false);
    this.focused.next(false);
   }

   public cities: Observable<ExploreCityRef[]>;

   ngOnDestroy() {
    if (this.observerLeft) {
      this.observerLeft.disconnect();
      this.observerLeft = undefined;
    }

    if (this.observerRight) {
      this.observerRight.disconnect();
      this.observerRight = undefined;
    }
   }
}

